import {
    combineSlices,
    compose,
    configureStore,
    type ConfigureStoreOptions,
    type ThunkAction,
    type UnknownAction,
} from '@reduxjs/toolkit';
import { type TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import persistState from 'redux-sessionstorage';

import socketMiddleware from 'services/socketMiddleware';
import { announcementApi } from 'store/api/announcementApi';
import { auctionApi } from 'store/api/auctionApi';
import { authApi } from 'store/api/authApi';
import { claimApi } from 'store/api/claimApi';
import { clientApi } from 'store/api/clientApi';
import { medbillingApi } from 'store/api/medbillingApi';
import { notificationApi } from 'store/api/notificationApi';
import { onboardingApi } from 'store/api/onboardingApi';
import { performanceApi } from 'store/api/performanceApi';
import { rateSetterApi } from 'store/api/rateSetterApi';
import { referenceApi } from 'store/api/referenceApi';
import { scopeAssessorApi } from 'store/api/scopeAssessorApi';
import { treatmentApi } from 'store/api/treatmentApi';
import { userSupportApi } from 'store/api/userSupportApi';
import { worklistApi } from 'store/api/worklistApi';
import { authSlice } from 'store/auth/authSlice';
import { claimsListSlice } from 'store/claims/claimsListSlice';
import { gridsSlice } from 'store/grids/gridsSlice';
import { liveReferralsSlice } from 'store/liveReferrals/liveReferralsSlice';
import { notificationSlice } from 'store/notification/notificationSlice';
import { userSupportSlice } from 'store/userSupport/userSupportSlice';
import { performanceSlice } from 'store/wonReferrals/performanceSlice';
import { wonReferralListSlice } from 'store/wonReferrals/wonReferralListSlice';
import { worklistSlice } from 'store/worklist/worklistSlice';

const sessionStorageEnhancer = compose(persistState(gridsSlice.name, { key: 'marketplaceStore' }));

const slices = [
    authSlice,
    claimsListSlice,
    gridsSlice,
    liveReferralsSlice,
    notificationSlice,
    performanceSlice,
    wonReferralListSlice,
    worklistSlice,
    userSupportSlice,
];

const apiSlices = [
    announcementApi,
    auctionApi,
    authApi,
    claimApi,
    clientApi,
    medbillingApi,
    notificationApi,
    onboardingApi,
    performanceApi,
    rateSetterApi,
    referenceApi,
    scopeAssessorApi,
    treatmentApi,
    userSupportApi,
    worklistApi,
];

const rootReducer = combineSlices(...slices, ...apiSlices);

export const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({ serializableCheck: false })
            .concat(apiSlices.map((slice) => slice.middleware))
            .concat(socketMiddleware),
    enhancers: (getDefaultEnhancers) => getDefaultEnhancers().concat(sessionStorageEnhancer),
    devTools: process.env.REACT_APP_ENV !== 'prd',
});

export type AppDispatch = typeof store.dispatch;
export const useAppDispatch: () => AppDispatch = useDispatch;

export type RootState = ReturnType<typeof store.getState>;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export type AppThunk<ReturnType = void> = ThunkAction<
    ReturnType,
    RootState,
    unknown,
    UnknownAction
>;

/** Used for configuration of redux store in tests */
export const MockStore: ConfigureStoreOptions = {
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({ serializableCheck: false })
            .concat(apiSlices.map((slice) => slice.middleware))
            .concat(socketMiddleware),
};
